import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { InvestAccountContext } from "./contexts/InvestAccountContext";
import { InvestTransactionContext } from "./contexts/InvestTransactionContext";
import { InvestCardComponent } from "./InvestCardComponent";
import AreYouSureModal from "../../../../components/modals/areYouSureModal";
import { Box, FormControlLabel, Radio, Stack, Typography, Button, Backdrop, CircularProgress } from "@mui/material";
import Tabs from "../../../../components/tabs/tabs";
import { SharedBlockChainAccCreation } from "../../../../components/modals/common/SharedBlockChainAccCreation";
import ModalComponent from "../../../../components/modals/modalComponent";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
export const DLTAccountsCard = React.memo(() => {
    const {
        availableBlockchainAccounts,
        selectedBlockchainAccountIndex,
        setSelectedBlockchainAccountIndex,
        changeBlockchainAccountModalOpen,
        setChangeBlockchainAccountModalOpen,
        selectedBlockChainAccount,
        setSelectedBloackChainAccount,
        chosenDltAccountAfterCommitment,
        accountSelected,
        entityId,
        refreshDltWallet
    } = useContext(InvestAccountContext);
    const { selectedMode, listing } = useContext(InvestTransactionContext);
    const { enqueueSnackbar } = useSnackbar();
    const user = useSelector(state => state.user.data);
    const [newBlockchainAccountIndex, setNewBlockchainAccountIndex] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [openCreateDLTModal, setOpenCreateDLTModal] = useState(false);
    const [blockchainAccountDownload, setBlockchainAccountDownload] = useState(false);
    const [blockchainAccountCreated, setBlockchainAccountCreated] = useState(false);
    const [animating, setAnimating] = useState(false);
    const [openProgressBar, setOpenProgressBar] = useState(false);
    const [sendMail, setSendMail] = useState(true);
    const modelRef = useRef();
    const handleChange = () => {
        setSelectedBlockchainAccountIndex(newBlockchainAccountIndex)
        setSelectedBloackChainAccount(availableBlockchainAccounts[newBlockchainAccountIndex]?.publicAddress);
    };

    const customHandleClose = (newDltAccountAddress) => {
        if (!blockchainAccountCreated || blockchainAccountDownload) {
            setOpenCreateDLTModal(false);
            setBlockchainAccountCreated(false);
            // Refresh blockchain accounts after creation
            const idToRefresh = entityId || user?._id;
            if (idToRefresh && refreshDltWallet) {
                refreshDltWallet(idToRefresh);
            }
        } else {
            enqueueSnackbar("Please download key details before closing!", { 
                variant: "error", 
                anchorOrigin: { horizontal: "right", vertical: "top" }, 
                autoHideDuration: 5000 
            });
        }
    };

    const scrollToTop = () => {
        if (modelRef.current) {
            let modalBody = modelRef.current.querySelector('.MuiDialog-container');
            if (modalBody) {
                modalBody.scroll({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        }
    };

    useEffect(() => {
        if (animating) {
            scrollToTop();
        }
    }, [animating]);

    // Filter accounts by network and create tabs only for networks that have accounts
    const networkTabs = useMemo(() => {
        const polygonAccounts = availableBlockchainAccounts?.filter(account => 
            account?.network?.toLowerCase().includes('polygon')
        ) || [];

        const mstAccounts = availableBlockchainAccounts?.filter(account => 
            account?.network?.toLowerCase().includes('mst')
        ) || [];
        
        const algorandAccounts = availableBlockchainAccounts?.filter(account => 
            account?.network?.toLowerCase().includes('algorand')
        ) || [];

        const tabs = [];
        if (polygonAccounts.length > 0) {
            tabs.push({ name: "Polygon", network: "polygon", accounts: polygonAccounts });
        }

        if (mstAccounts.length > 0) {
            tabs.push({ name: "MST", network: "mst", accounts: mstAccounts });
        }

        if (algorandAccounts.length > 0 && listing?.created_by?.role?.role!="realxWhiteBoxDeveloperAdmin") {
            tabs.push({ name: "Algorand", network: "algorand", accounts: algorandAccounts });
        }
        return tabs;
    }, [availableBlockchainAccounts]);

    const selectPrimaryForTab = (tabIndex) => {
        const tab = networkTabs[tabIndex];
        if (!tab?.accounts?.length) return;

        const account = tab.accounts.find((a) => a.isPrimary) || tab.accounts[0];
        const globalIndex = availableBlockchainAccounts.findIndex(
            (a) => a.publicAddress === account.publicAddress
        );
        if (globalIndex === -1) return;

        setSelectedBloackChainAccount(account.publicAddress);
        setNewBlockchainAccountIndex(globalIndex);
        setSelectedBlockchainAccountIndex(globalIndex);
    };

    const handleTabChange = (tabIndex) => {
        setActiveTab(tabIndex);
        if (chosenDltAccountAfterCommitment === undefined || chosenDltAccountAfterCommitment === null) {
            selectPrimaryForTab(tabIndex);
        }
    };

    // Reset activeTab if current tab becomes invalid
    useEffect(() => {
        if (activeTab >= networkTabs.length && networkTabs.length > 0) {
            setActiveTab(0);
        }
    }, [activeTab, networkTabs.length]);

    useEffect(() => {
        if ((chosenDltAccountAfterCommitment === undefined || chosenDltAccountAfterCommitment === null) && availableBlockchainAccounts?.length > 0) {
            // Pick primary Polygon account if exists, else primary Algorand account
            let selected = availableBlockchainAccounts.find(
                account => account.network?.toLowerCase() === "polygon" && account.isPrimary
            );
            if (!selected) {
                selected = availableBlockchainAccounts.find(
                    account => account.network?.toLowerCase() === "mst" && account.isPrimary
                );
            }
            if (!selected && listing?.created_by?.role?.role!="realxWhiteBoxDeveloperAdmin") {
                selected = availableBlockchainAccounts.find(
                    account => account.network?.toLowerCase() === "algorand" && account.isPrimary
                );
            }

            const selectedIndex = selected
                ? availableBlockchainAccounts.findIndex(a => a.publicAddress === selected.publicAddress)
                : 0;

            setSelectedBloackChainAccount(selected?.publicAddress||"");
            setNewBlockchainAccountIndex(selectedIndex);
            setSelectedBlockchainAccountIndex(selectedIndex);
            
            // Set active tab based on selected account's network
            if (selected) {
                const network = selected.network?.toLowerCase();
                const tabIndex = networkTabs.findIndex(tab => 
                    tab.network === network
                );
                if (tabIndex !== -1) {
                    setActiveTab(tabIndex);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableBlockchainAccounts, chosenDltAccountAfterCommitment, networkTabs]);

    return (
        <>
            {selectedMode === 0 && (
                <InvestCardComponent title={"Select DLT Account for Investment:"}>
                    {changeBlockchainAccountModalOpen && <AreYouSureModal
                        open={changeBlockchainAccountModalOpen}
                        setOpen={setChangeBlockchainAccountModalOpen}
                        handleYes={() => handleChange(selectedBlockchainAccountIndex)}
                        message={"Are You Sure About Changing DLT Account for this Transaction?"}
                        note={"New DLT Account is Only Applicable for This Specific Transaction."}
                        publicAddress={availableBlockchainAccounts[newBlockchainAccountIndex]?.publicAddress}
                        publicAddressMessage={"Your New DLT Account Address"}
                    />}
                    <Stack spacing={"20px"} direction="column">
                        {availableBlockchainAccounts?.length === 0 ? (<>
                            <Typography variant="body2">No available blockchain accounts</Typography>
                           
                            </>
                        ) : networkTabs.length === 0 ? (<>
                            <Typography variant="body2">No available blockchain accounts</Typography>
                            {listing?.created_by?.role?.role==="realxWhiteBoxDeveloperAdmin" &&<Typography variant="body2">To proceed with your investment, please link or create a Polygon DLT account. </Typography>}
                            {listing?.created_by?.role?.role==="realxWhiteBoxDeveloperAdmin" && <Box display="flex" justifyContent="center" mt={2}>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    size="small"
                                    onClick={() => {
                                        setOpenCreateDLTModal(true);
                                    }}
                                >
                                    Create DLT Account
                                </Button>
                            </Box>
                            }
                            </>
                        ) : (
                            <>
                                <Tabs 
                                    tabs={networkTabs.map(tab => ({ name: tab.name }))}
                                    activeTab={activeTab}
                                    onClick={handleTabChange}
                                />
                                <Box mt={2}>
                                    {networkTabs[activeTab] && networkTabs[activeTab].accounts.map((account, index) => {
                                        // Derive global index for Radio value and selection
                                        const globalIndex = availableBlockchainAccounts.findIndex(a => a.publicAddress === account.publicAddress);
                                        return (
                                            <Box key={account?.publicAddress} mb={1}>
                                                <FormControlLabel
                                                    sx={{ alignItems: 'center' }}
                                                    control={
                                                        <Radio
                                                            disabled={chosenDltAccountAfterCommitment && (chosenDltAccountAfterCommitment !== account?.publicAddress)}
                                                            checked={selectedBlockChainAccount === account?.publicAddress}
                                                            onChange={() => {
                                                                setNewBlockchainAccountIndex(globalIndex);
                                                                setChangeBlockchainAccountModalOpen(true);
                                                            }}
                                                            value={globalIndex}
                                                        />
                                                    }
                                                    label={
                                                        <Box display="flex" flexDirection="column">
                                                            <Typography variant="body1" fontWeight={400}>
                                                                ...{account?.publicAddress?.slice(-18)} - {account?.name?.slice(0, 20)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </>
                        )}
                    </Stack>
                </InvestCardComponent>
            )}
            {openCreateDLTModal && (
                <ModalComponent
                    slotProps={{
                        root: {
                            ref: modelRef
                        }
                    }}
                    open={openCreateDLTModal}
                    handleClose={customHandleClose}
                    contentPadding={{ xs: 1, md: 1.25 }}
                    PaperProps={{ sx: { textAlign: "center", overflow: "auto", width: { md: 480 }, maxWidth: "100%" } }}
                >
                    <Backdrop open={openProgressBar} sx={{ color: "#ddd", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                        <CircularProgress
                            size={75}
                            thickness={4}
                            sx={{
                                position: "fixed",
                                left: "50%",
                                top: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 2
                            }}
                            style={{ color: "#fff" }}
                        />
                    </Backdrop>
                    <SharedBlockChainAccCreation
                        entity={entityId||user}
                        entityType={accountSelected==="Personal"?"INVESTOR":accountSelected==="Trust"?"TRUST":accountSelected==="HUF"?"HUF":"FAMILYMEMBER"}
                        customHandleClose={customHandleClose}
                        setAnimating={setAnimating}
                        setBlockchainAccountDownload={setBlockchainAccountDownload}
                        blockchainAccountCreated={blockchainAccountCreated}
                        setBlockchainAccountCreated={setBlockchainAccountCreated}
                        onAccountCreated={() => {
                            const idToRefresh = entityId || user?._id;
                            if (idToRefresh && refreshDltWallet) {
                                refreshDltWallet(idToRefresh);
                            }
                        }}
                        onAccountDownloaded={() => {
                            const idToRefresh = entityId || user?._id;
                            if (idToRefresh && refreshDltWallet) {
                                refreshDltWallet(idToRefresh);
                            }
                        }}
                        disableAlgorandOption={true}
                        disablePolygonOption={false}
                        disableMstOption={false}
                        nicknamePlaceholder="Secondary Account"
                        sendMail={sendMail}
                        setSendMail={setSendMail}
                    />
                </ModalComponent>
            )}
        </>
    );
});
